import { useState } from 'react';
import { cn } from '@/lib/utils';
import { getPresetAmounts, toMajorUnit, toSmallestUnit } from '@/lib/utils/currency';
import type { Currency } from '@/types/donation';
import Input from '@/components/ui/Input';

interface AmountSelectorProps {
  value: number; // In smallest unit (cents, kobo)
  onChange: (amount: number) => void; // Passes smallest unit
  currency: Currency;
  currencySymbol: string;
  minimumAmount: number; // In smallest unit
  error?: string;
}

function AmountSelector({
  value,
  onChange,
  currency,
  currencySymbol,
  minimumAmount,
  error,
}: AmountSelectorProps) {
  const [isCustom, setIsCustom] = useState(false);
  const presets = getPresetAmounts(currency);

  const handlePresetClick = (amount: number) => {
    setIsCustom(false);
    onChange(amount);
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    onChange(0);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = parseFloat(inputValue);

    if (isNaN(numericValue) || inputValue === '') {
      onChange(0);
    } else {
      // Convert from major unit to smallest unit
      onChange(toSmallestUnit(numericValue));
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-300 uppercase tracking-wider">
        Donation Amount
        <span className="text-gold ml-1">*</span>
      </label>

      {/* Preset Amount Buttons */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => handlePresetClick(preset)}
            className={cn(
              'px-4 py-4 rounded-lg border-2 font-semibold transition-all duration-200',
              'hover:border-gold hover:bg-gold/10',
              'focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-dark',
              !isCustom && value === preset
                ? 'border-gold bg-gold/20 text-gold'
                : 'border-dark-border bg-dark-card text-gray-300'
            )}
            aria-pressed={!isCustom && value === preset}
          >
            {currencySymbol}
            {toMajorUnit(preset).toLocaleString()}
          </button>
        ))}
      </div>

      {/* Custom Amount Button */}
      <button
        type="button"
        onClick={handleCustomClick}
        className={cn(
          'w-full px-4 py-4 rounded-lg border-2 font-semibold transition-all duration-200',
          'hover:border-gold hover:bg-gold/10',
          'focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-dark',
          isCustom
            ? 'border-gold bg-gold/20 text-gold'
            : 'border-dark-border bg-dark-card text-gray-300'
        )}
        aria-pressed={isCustom}
      >
        Custom Amount
      </button>

      {/* Custom Amount Input */}
      {isCustom && (
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
            {currencySymbol}
          </div>
          <Input
            type="number"
            inputMode="decimal"
            placeholder={`Minimum ${currencySymbol}${toMajorUnit(minimumAmount)}`}
            value={value > 0 ? toMajorUnit(value) : ''}
            onChange={handleCustomAmountChange}
            min={toMajorUnit(minimumAmount)}
            step="0.01"
            className="pl-12 bg-dark-card border-dark-border text-gray-100"
            error={error}
            aria-label="Custom donation amount"
          />
        </div>
      )}

      {/* Display error for preset amounts */}
      {!isCustom && error && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {/* Helper text */}
      <p className="text-sm text-gray-500">
        Minimum donation: {currencySymbol}
        {toMajorUnit(minimumAmount).toLocaleString()}
      </p>
    </div>
  );
}

export default AmountSelector;
